import styled, { css } from 'styled-components';

// Styled Components
import {
  Tr,
  TableContainer,
  FiltersContainer,
  SubContainerFilters,
  Td,
  DropDownInputStyled,
  SearchableDropdownStyled,
  MultiSelectDropdownStyled,
  InputCalendarStyled as InputCalendar,
  ContainerButtons,
  Container,
} from '../components/MainStyledComponents';

// Types
import {
  ArrowBottomStyledProps,
  ContainersLoadingProps,
  ShowFiltersButtonProps,
} from './types';

// Icons
import { ArrowBottom } from '../../../../assets/icons/ArrowBottom';

// Components
import { ExternalCompaniesCell } from '../../ExternalCompaniesCell';

export const ContainerStyled = styled(Container)`
  position: relative;
`;

export const TableContainerStyled = styled(
  TableContainer
)<ContainersLoadingProps>`
  opacity: ${(props) => (props.isLoading ? 0.6 : 1)};
`;

export const FiltersWithLoadingStyled = styled(
  FiltersContainer
)<ContainersLoadingProps>`
  opacity: ${(props) => (props.isLoading ? 0.6 : 1)};
`;

export const ContainerLoader = styled.div`
  position: absolute;
  top: 30%;
  left: 50%;
  z-index: 1;
`;

export const SubContainerHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const ShowFiltersButton = styled.button<ShowFiltersButtonProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  height: 2.3rem;
  border-radius: 0.3rem;
  transition: background 300ms;
  background: ${(props) =>
    props.isBlack ? props.theme.colors.black : props.theme.colors.garnet};
`;

export const ArrowBottomStyled = styled(ArrowBottom)<ArrowBottomStyledProps>`
  transition: transform 300ms;
  transform: ${(props) => (props.isRotate ? null : 'rotate(-90deg)')};
  width: 1rem;
  height: 1rem;
  path {
    fill: white;
  }
`;

export const SubContainerFiltersStyled = styled(SubContainerFilters)``;

export const TdStyled = styled(Td)``;

export const TrStyled = styled(Tr)`
  background-color: ${(props) => props.checked && props.theme.colors.lightRed};
  ${({ indeterminate, checked }) => css`
    Td {
      ${!indeterminate && {
        opacity: 0.2,
      }}
      ${checked && {
        opacity: 1,
      }}
    }
  `}
`;

export const DropDownInputStyledBig = styled(DropDownInputStyled)`
  @media (max-width: ${({ theme }) =>
      theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    max-width: 21rem;
  }
  @media (max-width: ${({ theme }) =>
      theme.breakPoints.portraitTablet * theme.percentBaseResponsive}rem) {
    max-width: 16rem;
  }
`;

export const SearchableDropdownStyledBig = styled(SearchableDropdownStyled)`
  @media (max-width: ${({ theme }) =>
      theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    max-width: 21rem;
  }
  @media (max-width: ${({ theme }) =>
      theme.breakPoints.portraitTablet * theme.percentBaseResponsive}rem) {
    max-width: 16rem;
  }
`;

export const MultiSelectDropdownStyledBig = styled(MultiSelectDropdownStyled)`
  @media (max-width: ${({ theme }) =>
      theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    max-width: 21rem;
  }
  @media (max-width: ${({ theme }) =>
      theme.breakPoints.portraitTablet * theme.percentBaseResponsive}rem) {
    max-width: 16rem;
  }
`;

export const InputCalendarStyled = styled(InputCalendar)`
  @media (max-width: ${({ theme }) =>
      theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    margin-right: 0;
  }
  @media (max-width: ${({ theme }) =>
      theme.breakPoints.portraitTablet * theme.percentBaseResponsive}rem) {
    margin-right: 3rem;
  }
`;

export const ContainerButtonStyled = styled(ContainerButtons)`
  margin-left: -0.6rem;
`;

// Celda de estado: el SpecialTag y (si aplica) el badge de actuación simultánea
// conviven en una fila con wrap para no desbordar la columna.
export const StateCellWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
`;

// Badge "Simultánea": pill con borde/texto morado (mismo acento que el chip del
// informe Gestión Horaria), corto para no robar ancho a la columna de estado.
export const SimultaneousBadge = styled.span`
  border: 0.1rem solid ${({ theme }) => theme.colors.purple};
  color: ${({ theme }) => theme.colors.purple};
  border-radius: 0.4rem;
  padding: 0.1rem 0.4rem;
  font-size: 1rem;
  line-height: 1.4;
  white-space: nowrap;
`;

/**
 * Celda de la columna Materiales.
 *
 * Los nombres del catálogo son largos («Aglomerat en fred en pots de 25 Kg») y
 * la columna se dimensionaba por su contenido: en una página sin materiales
 * colapsaba a ~138 px y el texto de las que sí tienen salía partido en cuatro
 * líneas. El `size` de la definición de columna no lo arreglaba —esta tabla no
 * usa layout fijo, así que no llega a aplicarse—, de modo que el ancho se pide
 * desde la celda.
 *
 * 38rem son los mismos 380 px que ocupa Operarios, que es la referencia que
 * pidió el usuario.
 */
export const MaterialsCellStyled = styled(ExternalCompaniesCell)`
  /* Ancho fijo, no solo mínimo: con el texto en una línea el nombre más largo
     del catálogo («Càmera de TV compacte digital color amb sensor CCD D1/3…»)
     estiraba la columna hasta 488 px. Los dos topes la dejan en los mismos
     380 px que ocupa Operarios. */
  min-width: 38rem;
  max-width: 38rem;

  /* Una sola línea, como Operarios.
     Esa columna usa TextCell (nowrap + puntos suspensivos) mientras que la
     celda de lista envuelve, así que dos materiales bastaban para partir la
     fila en dos renglones y las filas de la tabla no quedaban a la misma
     altura. El min-width 0 es imprescindible: el texto es hijo de un
     contenedor flex y sin él no encoge por debajo de su contenido, con lo que
     desbordaría en vez de recortarse. El nombre completo sigue accesible: en
     el title cuando son pocos, y en el tooltip pulsable cuando hay varios. */
  white-space: nowrap;

  /* Hay DOS formas de celda y hay que cubrir las dos, porque el texto cuelga
     de sitios distintos:
       - pocos materiales -> el parrafo es hijo directo del contenedor;
       - varios materiales -> el parrafo vive dentro del contenedor del icono
         de ayuda, así que un selector de hijo directo NO lo alcanza. Ese era
         el caso que seguía desbordando y pisando la columna de al lado.

     El recorte va SIEMPRE en el párrafo, nunca en un contenedor: el aviso
     emergente se posiciona respecto al contenedor del icono, así que un
     overflow ahí lo dejaría cortado justo cuando se abre.

     Y no basta con el recorte por número de caracteres que ya hace el
     componente: cuenta letras, no píxeles, así que un nombre largo cabía en
     el conteo y no en la celda. */
  > p,
  > span,
  > div > p,
  > div > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }

  > div {
    min-width: 0;
    max-width: 100%;
  }
`;
